/**
 * Money, dates, times and numbers, formatted for the venue.
 *
 * Two rules from CLAUDE.md are enforced here rather than trusted:
 *
 *  - Money is an integer in the currency's minor unit plus an ISO 4217 code. `formatMoney` refuses
 *    a float, and the minor units are turned into a decimal *string* that Intl formats exactly, so
 *    no amount ever goes through binary floating point.
 *  - The venue's timezone decides the clock and the business day. Every date and time formatter
 *    takes the venue's `timeZone`, never the reader's, and never the server's.
 *
 * Instants on the wire are RFC 3339 in UTC; these helpers accept that string, a Date, or epoch
 * milliseconds.
 */
const toDate = (instant) => {
    const date = instant instanceof Date ? instant : new Date(instant);
    if (Number.isNaN(date.getTime()))
        throw new TypeError(`Not an instant: ${String(instant)}`);
    return date;
};
const memo = (fn) => {
    const cache = new Map();
    return (...args) => {
        const key = JSON.stringify(args);
        const hit = cache.get(key);
        if (hit !== undefined)
            return hit;
        const made = fn(...args);
        cache.set(key, made);
        return made;
    };
};
// Intl formatters are expensive to construct and immutable once built.
const numberFormat = memo((locale, options) => new Intl.NumberFormat(locale, options));
const dateTimeFormat = memo((locale, options) => new Intl.DateTimeFormat(locale, options));
const relativeFormat = memo((locale, options) => new Intl.RelativeTimeFormat(locale, options));
const listFormat = memo((locale, options) => new Intl.ListFormat(locale, options));
/**
 * How many minor units make one major unit, per ISO 4217 as the runtime knows it: 2 for INR, 0 for
 * JPY, 3 for KWD.
 */
export const minorUnitDigits = memo((currency) => {
    const resolved = numberFormat('en', { style: 'currency', currency }).resolvedOptions();
    return resolved.maximumFractionDigits ?? 2;
});
/**
 * Integer minor units to an exact decimal string. `15000` with 2 digits is `"150.00"`; `-5` with 2
 * digits is `"-0.05"`. No division, so no rounding and no float.
 */
export function minorUnitsToDecimalString(amount, digits) {
    if (!Number.isInteger(amount)) {
        throw new TypeError(`Money is an integer in the currency's minor unit, never a float (spec/06 §2): got ${amount}`);
    }
    if (digits === 0)
        return String(amount);
    const sign = amount < 0 ? '-' : '';
    const padded = String(Math.abs(amount)).padStart(digits + 1, '0');
    return `${sign}${padded.slice(0, -digits)}.${padded.slice(-digits)}`;
}
/**
 * Build the formatters for one venue. Cheap to call and safe to keep: the underlying Intl objects
 * are shared.
 */
export function createVenueFormatter(venue, options = {}) {
    const locale = options.locale ?? venue.locale;
    const timeZone = venue.timeZone;
    const moneyOptions = (currency, style) => {
        const digits = minorUnitDigits(currency);
        const base = { style: 'currency', currency };
        if (style === 'code')
            return { ...base, currencyDisplay: 'code' };
        if (style === 'compact')
            return { ...base, notation: 'compact', maximumFractionDigits: 1 };
        if (style === 'noFraction') {
            return { ...base, minimumFractionDigits: 0, maximumFractionDigits: 0 };
        }
        return { ...base, minimumFractionDigits: digits, maximumFractionDigits: digits };
    };
    // Intl.NumberFormat takes a numeric *string* for arbitrary precision (Intl.NumberFormat V3), so
    // the decimal never becomes a JS number. TypeScript types that parameter as a template literal,
    // which a computed string cannot satisfy, hence the assertion on a value we just built.
    const moneyValue = (money) => minorUnitsToDecimalString(money.amount, minorUnitDigits(money.currency));
    return {
        venue,
        locale,
        money(money, style = 'standard') {
            return numberFormat(locale, moneyOptions(money.currency, style)).format(moneyValue(money));
        },
        moneyParts(money, style = 'standard') {
            return numberFormat(locale, moneyOptions(money.currency, style)).formatToParts(moneyValue(money));
        },
        number(value, numberOptions = {}) {
            return numberFormat(locale, numberOptions).format(value);
        },
        percent(fraction, fractionDigits = 0) {
            return numberFormat(locale, {
                style: 'percent',
                minimumFractionDigits: fractionDigits,
                maximumFractionDigits: fractionDigits,
            }).format(fraction);
        },
        date(instant, style = 'medium') {
            return dateTimeFormat(locale, { dateStyle: style, timeZone }).format(toDate(instant));
        },
        time(instant, style = 'short') {
            return dateTimeFormat(locale, { timeStyle: style, timeZone }).format(toDate(instant));
        },
        dateTime(instant, style = {}) {
            return dateTimeFormat(locale, {
                dateStyle: style.date ?? 'medium',
                timeStyle: style.time ?? 'short',
                timeZone,
            }).format(toDate(instant));
        },
        custom(instant, dateOptions) {
            return dateTimeFormat(locale, { ...dateOptions, timeZone }).format(toDate(instant));
        },
        businessDate(instant) {
            // en-CA formats as YYYY-MM-DD; the parts are read back explicitly so a runtime that
            // disagrees cannot quietly produce a different key.
            const parts = dateTimeFormat('en-CA', {
                timeZone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            }).formatToParts(toDate(instant));
            const part = (type) => parts.find((p) => p.type === type)?.value ?? '';
            return `${part('year')}-${part('month')}-${part('day')}`;
        },
        relative(instant, now = Date.now()) {
            const deltaSeconds = Math.round((toDate(instant).getTime() - toDate(now).getTime()) / 1000);
            const units = [
                ['year', 31_536_000],
                ['month', 2_592_000],
                ['week', 604_800],
                ['day', 86_400],
                ['hour', 3600],
                ['minute', 60],
                ['second', 1],
            ];
            const format = relativeFormat(locale, { numeric: 'auto' });
            for (const [unit, size] of units) {
                if (Math.abs(deltaSeconds) >= size || unit === 'second') {
                    return format.format(Math.trunc(deltaSeconds / size), unit);
                }
            }
            return format.format(0, 'second');
        },
        duration(seconds, style = 'short') {
            if (!Number.isInteger(seconds)) {
                throw new TypeError(`A duration is a whole number of seconds: got ${seconds}`);
            }
            const total = Math.abs(seconds);
            const hours = Math.floor(total / 3600);
            const minutes = Math.floor((total % 3600) / 60);
            const rest = total % 60;
            if (style === 'clock') {
                const pad = (n) => numberFormat(locale, { minimumIntegerDigits: 2, useGrouping: false }).format(n);
                const head = numberFormat(locale, { useGrouping: false }).format(hours);
                return hours > 0 ? `${head}:${pad(minutes)}:${pad(rest)}` : `${pad(minutes)}:${pad(rest)}`;
            }
            const unit = (value, name) => numberFormat(locale, { style: 'unit', unit: name, unitDisplay: 'narrow' }).format(value);
            const pieces = [];
            if (hours > 0)
                pieces.push(unit(hours, 'hour'));
            if (minutes > 0)
                pieces.push(unit(minutes, 'minute'));
            if (pieces.length === 0)
                pieces.push(unit(rest, 'second'));
            return listFormat(locale, { style: 'narrow', type: 'unit' }).format(pieces);
        },
    };
}
//# sourceMappingURL=format.js.map